import { create } from 'zustand'
import {
  PartnerTier,
  CreatePartnerTierRequest,
  UpdatePartnerTierRequest
} from '../types/partnerTier'
import { partnerTierService } from '../services/partnerTierService'

interface PartnerTierState {
  // State
  tiers: PartnerTier[]
  loading: boolean
  error: string | null
  selectedTier: PartnerTier | null

  // Actions
  loadTiers: (isActive?: boolean) => Promise<void>
  createTier: (data: CreatePartnerTierRequest) => Promise<PartnerTier>
  updateTier: (id: string, data: UpdatePartnerTierRequest) => Promise<PartnerTier>
  setSelectedTier: (tier: PartnerTier | null) => void
  reset: () => void
}

const usePartnerTierStore = create<PartnerTierState>((set) => ({
  // Initial State
  tiers: [],
  loading: false,
  error: null,
  selectedTier: null,

  // Actions
  loadTiers: async (isActive?: boolean) => {
    set({ loading: true, error: null })
    try {
      const tiers = await partnerTierService.getTiers(isActive)
      set({ tiers, loading: false })
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to load tiers',
        loading: false
      })
    }
  },

  createTier: async (data: CreatePartnerTierRequest) => {
    const tier = await partnerTierService.createTier(data)

    // Add to local state
    set((state) => ({
      tiers: [...state.tiers, tier].sort((a, b) => a.name.localeCompare(b.name))
    }))

    return tier
  },

  updateTier: async (id: string, data: UpdatePartnerTierRequest) => {
    const updated = await partnerTierService.updateTier(id, data)

    // Update local state
    set((state) => ({
      tiers: state.tiers
        .map((tier) => (tier.id === id ? updated : tier))
        .sort((a, b) => a.name.localeCompare(b.name)),
      selectedTier: state.selectedTier?.id === id ? updated : state.selectedTier
    }))

    return updated
  },

  setSelectedTier: (tier: PartnerTier | null) => {
    set({ selectedTier: tier })
  },

  reset: () =>
    set({
      tiers: [],
      loading: false,
      error: null,
      selectedTier: null
    })
}))

export default usePartnerTierStore
